"use client";

import { useState } from "react";
import Link from "next/link";
import {
  BLOG_POSTS,
  CATEGORY_LABELS,
  CATEGORY_THUMB,
  type BlogCategory,
  type BlogPost,
} from "@/lib/blog";


const TABS: { id: BlogCategory | "all"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "winter", label: "❄️ Winter" },
  { id: "summer", label: "☀️ Summer" },
  { id: "tips", label: "📋 Tips & Advice" },
  { id: "guide", label: "📍 Resort Guides" },
];

const RESORT_GUIDES = [
  { flag: "🇫🇷", name: "Méribel", area: "Three Valleys", href: "/guides/meribel" },
  { flag: "🇫🇷", name: "Val d'Isère", area: "Espace Killy", href: "/guides/val-disere" },
  { flag: "🇫🇷", name: "Morzine", area: "Portes du Soleil", href: "/guides/morzine" },
  { flag: "🇫🇷", name: "Chamonix", area: "Mont Blanc Valley", href: "/guides/chamonix" },
  { flag: "🇨🇭", name: "Verbier", area: "4 Vallées", href: "/guides/verbier" },
  { flag: "🇨🇦", name: "Whistler", area: "British Columbia", href: "/guides/whistler" },
  { flag: "🇪🇸", name: "Ibiza", area: "Balearics", href: "/guides/ibiza" },
  { flag: "🇬🇷", name: "Mykonos", area: "Cyclades", href: "/guides/mykonos" },
  { flag: "🇭🇷", name: "Croatia", area: "Adriatic Coast", href: "/guides/croatia" },
  { flag: "🏝️", name: "Caribbean", area: "Yacht Season", href: "/guides/caribbean" },
  { flag: "🇫🇷", name: "Antibes", area: "French Riviera", href: "/guides/antibes" },
  { flag: "🇪🇸", name: "Palma", area: "Mallorca", href: "/guides/palma" },
  { flag: "🇺🇸", name: "Fort Lauderdale", area: "Florida", href: "/guides/fort-lauderdale" },
];

const JOB_GUIDES = [
  { name: "Yacht Crew (No Experience)", href: "/guides/yacht-crew-jobs-no-experience" },
  { name: "Surf Instructor Jobs", href: "/guides/surf-instructor-jobs" },
  { name: "UK Festival Jobs", href: "/festivals/festival-jobs-uk" },
  { name: "Glastonbury Jobs", href: "/festivals/glastonbury-jobs" },
  { name: "Festival Catering Jobs", href: "/festivals/festival-catering-jobs" },
  { name: "Boomtown Fair Jobs", href: "/festivals/boomtown-jobs" },
  { name: "Summer Jobs in Greece", href: "/guides/greece-summer-jobs" },
  { name: "Summer Jobs in Portugal", href: "/guides/portugal-summer-jobs" },
];

const PRACTICAL_GUIDES = [
  { name: "No Experience? No Problem", href: "/guides/season-job-no-experience" },
  { name: "Writing a Seasonal CV", href: "/guides/how-to-write-a-seasonal-cv" },
  { name: "Visa & Right to Work Guide", href: "/guides/seasonal-work-visa-guide" },
  { name: "Packing for a Season", href: "/guides/packing-for-a-season" },
  { name: "What to Wear Skiing", href: "/guides/what-to-wear-skiing" },
  { name: "UK Festival Jobs Overview", href: "/festivals/uk-festivals" },
];

function ArticleCard({ post }: { post: BlogPost }) {
  return (
    <Link
      href={`/blog/${post.slug}`}
      className="bg-white rounded-2xl overflow-hidden border border-border-light no-underline text-charcoal transition-all hover:-translate-y-1 hover:shadow-[0_8px_28px_rgba(0,0,0,0.05)]"
    >
      <div
        className={`h-40 flex items-center justify-center text-5xl ${CATEGORY_THUMB[post.category]}`}
      >
        {post.emoji}
      </div>
      <div className="p-5">
        <div className="text-[11px] font-semibold text-gold uppercase tracking-[1.5px] mb-2">
          {CATEGORY_LABELS[post.category]}
        </div>
        <h4 className="text-base font-semibold leading-[1.3] mb-2">
          {post.title}
        </h4>
        <p className="text-[13px] text-text-muted leading-[1.6] line-clamp-2">
          {post.excerpt}
        </p>
        <div className="text-xs text-text-muted mt-3">{post.readTime}</div>
      </div>
    </Link>
  );
}

export function BlogIndex() {
  const [activeTab, setActiveTab] = useState<BlogCategory | "all">("all");

  const featured = BLOG_POSTS.find((p) => p.featured);
  const articles =
    activeTab === "all"
      ? BLOG_POSTS.filter((p) => !p.featured)
      : BLOG_POSTS.filter(
          (p) => p.category === activeTab && !p.featured
        );

  return (
    <>
      {/* Category tabs */}
      <div className="flex gap-1 justify-center mx-5 md:mx-auto mb-10 md:mb-14 bg-white rounded-[10px] p-1 w-fit border border-border-light flex-wrap">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 md:px-6 py-2.5 rounded-sm text-[13px] font-semibold cursor-pointer border-none font-sans transition-all ${
              activeTab === tab.id
                ? "bg-navy text-white shadow-[0_2px_8px_rgba(27,42,74,0.2)]"
                : "bg-transparent text-text-muted"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Featured post */}
      {featured && activeTab === "all" && (
        <div className="max-w-[1000px] mx-auto px-5 md:px-12 pb-10 md:pb-14">
          <Link
            href={`/blog/${featured.slug}`}
            className="grid md:grid-cols-2 bg-white rounded-[22px] overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.04)] border border-border-light no-underline text-charcoal transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(0,0,0,0.08)]"
          >
            <div className="bg-gradient-to-br from-navy via-navy-mid to-sage flex items-center justify-center text-[80px] min-h-[240px] md:min-h-[320px]">
              {featured.emoji}
            </div>
            <div className="p-7 md:p-10 flex flex-col justify-center">
              <div className="text-[11px] font-semibold text-gold uppercase tracking-[1.5px] mb-3">
                {CATEGORY_LABELS[featured.category]}
              </div>
              <h2 className="font-serif text-[24px] md:text-[28px] font-bold leading-[1.2] tracking-tight mb-3">
                {featured.title}
              </h2>
              <p className="text-sm text-text-muted leading-[1.7] mb-5">
                {featured.excerpt}
              </p>
              <div className="text-xs text-text-muted flex gap-4">
                <span>📅 {featured.readTime}</span>
                <span>👤 {featured.author}</span>
              </div>
            </div>
          </Link>
        </div>
      )}

      {/* Articles grid */}
      <div className="max-w-[1000px] mx-auto px-5 md:px-12 pb-12 md:pb-16">
        <h3 className="font-serif text-2xl mb-7">
          {activeTab === "all" ? "Latest Articles" : TABS.find((t) => t.id === activeTab)?.label}
        </h3>
        {articles.length > 0 ? (
          <div className="grid md:grid-cols-3 gap-5">
            {articles.map((post) => (
              <ArticleCard key={post.slug} post={post} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-text-muted">
            No articles in this category yet. Check back soon!
          </p>
        )}
      </div>

      {/* Resort & destination guides */}
      <div className="max-w-[1000px] mx-auto px-5 md:px-12 pb-10 md:pb-14">
        <div className="flex items-baseline justify-between mb-7">
          <h3 className="font-serif text-2xl">Destination Guides</h3>
          <Link
            href="/guides"
            className="text-[13px] font-medium text-gold no-underline hover:underline"
          >
            View all guides
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {RESORT_GUIDES.map((resort) => (
            <Link
              key={resort.name}
              href={resort.href}
              className="bg-white rounded-md p-6 text-center border border-border-light no-underline text-charcoal transition-all hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(0,0,0,0.05)] hover:border-gold/20"
            >
              <div className="text-[32px] mb-2">{resort.flag}</div>
              <h4 className="text-sm font-semibold mb-0.5">{resort.name}</h4>
              <p className="text-xs text-text-muted">{resort.area}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Job guides */}
      <div className="max-w-[1000px] mx-auto px-5 md:px-12 pb-10 md:pb-14">
        <h3 className="font-serif text-2xl mb-7">Job Guides</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {JOB_GUIDES.map((guide) => (
            <Link
              key={guide.name}
              href={guide.href}
              className="bg-white rounded-md px-5 py-4 border border-border-light no-underline text-charcoal transition-all hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(0,0,0,0.05)] hover:border-gold/20"
            >
              <h4 className="text-sm font-semibold">{guide.name}</h4>
            </Link>
          ))}
        </div>
      </div>

      {/* Practical guides */}
      <div className="max-w-[1000px] mx-auto px-5 md:px-12 pb-12 md:pb-16">
        <h3 className="font-serif text-2xl mb-7">Practical Guides</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {PRACTICAL_GUIDES.map((guide) => (
            <Link
              key={guide.name}
              href={guide.href}
              className="bg-white rounded-md px-5 py-4 border border-border-light no-underline text-charcoal transition-all hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(0,0,0,0.05)] hover:border-gold/20"
            >
              <h4 className="text-sm font-semibold">{guide.name}</h4>
            </Link>
          ))}
        </div>
      </div>

    </>
  );
}
